import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api-mysql';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import getImageUrl from '../utils/imageUrl';

const ManageAchievers = ({ onAchieverUpdated }) => {
  const courseOptions = [
    'Project Management Professional (PMP)®',
    'Certified Associate in Project Management (CAPM)®',
    'Program Management Professional (PgMP)®',
    'Portfolio Management Professional (PfMP)®',
    'PMI Agile Certified Practitioner (PMI-ACP)®',
    'PMI Risk Management Professional (PMI-RMP)®',
    'PMI Professional in Business Analysis (PMI-PBA)®'
  ];
  const [achievers, setAchievers] = useState([]);
  const [filteredAchievers, setFilteredAchievers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [editingAchiever, setEditingAchiever] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [croppedEditImage, setCroppedEditImage] = useState(null);
  const [editCropper, setEditCropper] = useState(null);
  const [showEditCropper, setShowEditCropper] = useState(false);
  const [editCropAspect, setEditCropAspect] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getAchieverDateValue = (achiever) => {
    if (achiever?.date) {
      return achiever.date.split('T')[0];
    }

    if (achiever?.month && achiever?.year) {
      const monthIndex = new Date(`${achiever.month} 1, ${achiever.year}`).getMonth();
      const month = String(monthIndex + 1).padStart(2, '0');
      return `${achiever.year}-${month}-01`;
    }

    return '';
  };

  const formatAchieverDate = (achiever) => {
    const dateValue = getAchieverDateValue(achiever);
    if (!dateValue) {
      return `${achiever.month} ${achiever.year}`;
    }

    const [year, month, day] = dateValue.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US');
  };

  useEffect(() => {
    fetchAchievers();
  }, []);

  useEffect(() => {
    filterAchievers();
  }, [achievers, searchTerm, selectedYear, selectedMonth, companyFilter, selectedCourse]);

  const fetchAchievers = async () => {
    try {
      const achieversResponse = await api.get('/achievers');
      setAchievers(achieversResponse.data);
    } catch (error) {
      console.error('Error fetching achievers:', error);
    }
  };

  const filterAchievers = () => {
    let filtered = achievers.filter(achiever => {
      const name = (achiever.name || "").toLowerCase();
      const company = (achiever.company || "").toLowerCase();
      const role = (achiever.role || "").toLowerCase();
      const course = (achiever.course || "").toLowerCase();
      
      const matchesSearch = !searchTerm ||
        name.includes(searchTerm.toLowerCase()) ||
        company.includes(searchTerm.toLowerCase()) ||
        role.includes(searchTerm.toLowerCase()) ||
        course.includes(searchTerm.toLowerCase());
      
      const matchesYear = !selectedYear || String(achiever.year) === selectedYear;
      const matchesMonth = !selectedMonth || achiever.month === selectedMonth;
      const matchesCompany = !companyFilter || company.includes(companyFilter.toLowerCase());
      const matchesCourse = !selectedCourse || achiever.course === selectedCourse;
      
      return matchesSearch && matchesYear && matchesMonth && matchesCompany && matchesCourse;
    });

    setFilteredAchievers(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
    setCompanyFilter('');
    setSelectedCourse('');
    setCurrentPage(1);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAchievers = filteredAchievers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAchievers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = (achiever) => {
    setEditingAchiever({
      ...achiever,
      date: getAchieverDateValue(achiever)
    });
    setEditImage(achiever.image);
    setCroppedEditImage(null);
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this achiever?')) {
      try {
        await api.delete(`/achievers/${id}`);
        fetchAchievers();
        onAchieverUpdated();
        
        // Show success message
        alert('Achiever deleted successfully!');
      } catch (error) {
        console.error('Error deleting achiever:', error);
        alert('Failed to delete achiever');
      }
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(URL.createObjectURL(file));
      setShowEditCropper(true);
    }
  };

  const handleEditCrop = () => {
    if (editCropper) {
      setCroppedEditImage(editCropper.getCroppedCanvas({ maxWidth: 800, maxHeight: 800, imageSmoothingQuality: 'high' }).toDataURL('image/jpeg', 0.9));
      setShowEditCropper(false);
    }
  };

  const handleEditZoom = (ratio) => {
    if (editCropper) editCropper.zoom(ratio);
  };

  const handleEditRotate = (degree) => {
    if (editCropper) editCropper.rotate(degree);
  };

  const handleEditReset = () => {
    if (editCropper) editCropper.reset();
  };

  const handleEditAspectRatio = (ratio) => {
    setEditCropAspect(ratio);
    if (editCropper) editCropper.setAspectRatio(ratio);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('ManageAchievers - Editing achiever course:', editingAchiever.course);
      
      const data = new FormData();
      data.append('name', editingAchiever.name);
      data.append('company', editingAchiever.company);
      data.append('role', editingAchiever.role);
      data.append('course', editingAchiever.course || 'Project Management Professional (PMP)®');
      
      console.log('ManageAchievers - FormData course:', data.get('course'));
      data.append('year', editingAchiever.year);
      data.append('month', editingAchiever.month);
      data.append('date', editingAchiever.date);

      if (croppedEditImage) {
        const response = await fetch(croppedEditImage);
        const blob = await response.blob();
        data.append('image', blob, 'cropped-image.jpg');
      } else {
        data.append('image', editingAchiever.image);
      }

      await api.put(`/achievers/${editingAchiever.id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowEditModal(false);
      fetchAchievers();
      onAchieverUpdated();
      
      alert('Achiever updated successfully!');
    } catch (error) {
      console.error('Error updating achiever:', error);
      alert('Failed to update achiever');
    }
  };


  const renderAchieverCard = (achiever) => {
    const displayDate = formatAchieverDate(achiever);

    return (
      <div className="achiever-card mb-3 p-3 border rounded" key={achiever.id}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <img 
              src={getImageUrl(achiever.image)} 
              alt={achiever.name} 
              className="preview-image me-3"
              style={{width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px'}}
            />
            <div>
              <h5 className="mb-1 fw-bold">{achiever.name}</h5>
              <p className="mb-1 text-muted" style={{fontSize: '1rem'}}>
                <i className="bi bi-building me-1"></i>{achiever.company}
              </p>
              <p className="mb-1 text-muted" style={{fontSize: '1rem'}}>
                <i className="bi bi-briefcase me-1"></i>{achiever.role}
              </p>
              <p className="mb-1 text-dark" style={{fontSize: '0.95rem'}}>
                <i className="bi bi-award me-1"></i>{achiever.course}
              </p>
              <small className="text-primary">{achiever.month} {achiever.year}</small>
              <br />
              <small className="text-muted">
                <i className="bi bi-calendar-check me-1"></i>
                {displayDate}
              </small>
            </div>
          </div>
          <div>
            <button 
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => handleEdit(achiever)}
            >
              <i className="bi bi-pencil"></i> Edit
            </button>
            <button 
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(achiever.id)}
            >
              <i className="bi bi-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div>
      {/* Filter Section */}
      <div className="card border-0 shadow-sm bg-white rounded-3 mb-4">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">
            <i className="bi bi-funnel me-2"></i>Filter Achievers
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Search</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search by name, company, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Year</label>
              <select 
                className="form-select" 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">All Years</option>
                {[...new Set(achievers.map(a => a.year))].sort((a, b) => b - a).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Month</label>
              <select 
                className="form-select" 
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Company</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search company..."
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Course</label>
              <select
                className="form-select"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">All Courses</option>
                {courseOptions.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">&nbsp;</label>
              <div className="d-grid">
                <button className="btn btn-outline-secondary" onClick={clearFilters}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Clear
                </button>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <small className="text-muted">
              <span>{filteredAchievers.length}</span> Total Achievers
            </small>
          </div>
        </div>
      </div>

      {/* Achievers List */}
      <div className="card border-0 shadow-sm bg-white rounded-3">
        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-people me-2"></i>All Achievers
          </h5>
          {filteredAchievers.length > itemsPerPage && (
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i> Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
        
        <div className="card-body">
          {filteredAchievers.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-search display-4"></i>
              <h5 className="mt-3">No achievers found</h5>
            </div>
          ) : (
            currentAchievers.map(achiever => renderAchieverCard(achiever))
          )}
          
        </div>
        
      </div>

      {/* Pagination Controls */}
      {filteredAchievers.length > itemsPerPage && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAchievers.length)} of {filteredAchievers.length} achievers
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingAchiever && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil me-2"></i>Edit Achiever
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-semibold">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingAchiever.name}
                        onChange={(e) => setEditingAchiever({...editingAchiever, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-semibold">Company *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingAchiever.company}
                        onChange={(e) => setEditingAchiever({...editingAchiever, company: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-semibold">Role *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingAchiever.role}
                        onChange={(e) => setEditingAchiever({...editingAchiever, role: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-semibold">Course *</label>
                      <select
                        className="form-select"
                        value={editingAchiever.course || 'Project Management Professional (PMP)®'}
                        onChange={(e) => setEditingAchiever({...editingAchiever, course: e.target.value})}
                        required
                      >
                        {courseOptions.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Achievement Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={editingAchiever.date}
                        onChange={(e) => setEditingAchiever({...editingAchiever, date: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">Update Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleEditImageChange}
                      />
                      <small className="text-muted">Leave empty to keep current image</small>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Current Image</label>
                    <div className="text-center">
                      <div 
                        style={{cursor: 'pointer', display: 'inline-block', position: 'relative'}}
                        onClick={() => {
                          if (editImage) {
                            setShowEditCropper(true);
                          }
                        }}
                        title="Click to re-crop"
                      >
                        <img 
                          src={croppedEditImage || editImage} 
                          alt="Current" 
                          style={{maxWidth: '150px', maxHeight: '150px', borderRadius: '8px'}}
                        />
                        {editImage && (
                          <span className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '28px', height: '28px', fontSize: '0.8rem', border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.2)'}}>
                            <i className="bi bi-pencil-fill"></i>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleEditSubmit}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Cropper Modal */}
      {showEditCropper && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-crop me-2"></i>Crop Image
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditCropper(false)}></button>
              </div>
              <div className="modal-body p-0">
                <Cropper
                  src={editImage}
                  style={{ height: 500, width: '100%' }}
                  aspectRatio={editCropAspect}
                  guides={true}
                  viewMode={1}
                  dragMode="move"
                  scalable={true}
                  zoomable={true}
                  rotatable={true}
                  cropBoxMovable={true}
                  cropBoxResizable={true}
                  background={true}
                  responsive={true}
                  checkOrientation={false}
                  onInitialized={(instance) => setEditCropper(instance)}
                />
                <div className="d-flex flex-wrap justify-content-center gap-2 p-3 bg-light border-top">
                  <div className="btn-group btn-group-sm">
                    <button type="button" className={`btn ${editCropAspect === 1 ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => handleEditAspectRatio(1)} title="Square">
                      <i className="bi bi-square me-1"></i>1:1
                    </button>
                    <button type="button" className={`btn ${editCropAspect === 4/3 ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => handleEditAspectRatio(4/3)} title="Landscape">
                      <i className="bi bi-aspect-ratio me-1"></i>4:3
                    </button>
                    <button type="button" className={`btn ${editCropAspect === 3/4 ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => handleEditAspectRatio(3/4)} title="Portrait">
                      <i className="bi bi-phone me-1"></i>3:4
                    </button>
                    <button type="button" className={`btn ${editCropAspect === 0 ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => handleEditAspectRatio(0)} title="Free">
                      <i className="bi bi-arrows-fullscreen me-1"></i>Free
                    </button>
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => handleEditZoom(0.1)} title="Zoom In">
                      <i className="bi bi-zoom-in"></i>
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => handleEditZoom(-0.1)} title="Zoom Out">
                      <i className="bi bi-zoom-out"></i>
                    </button>
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => handleEditRotate(-90)} title="Rotate Left">
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => handleEditRotate(90)} title="Rotate Right">
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleEditReset} title="Reset">
                    <i className="bi bi-arrow-repeat me-1"></i>Reset
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditCropper(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleEditCrop}>
                  <i className="bi bi-check-lg me-2"></i>Apply Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAchievers;
